// src/components/section/OfficePersonalInfo.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const OfficePersonalInfo = ({ userData, setUserData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        contactNumber: "",
    });


    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("Authentication required.");
                setLoading(false);
                return;
            }

            try {
                const response = await api.get("/auth/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success && response.data.data?.user) {
                    const user = response.data.data.user;

                    setFormData({
                        name: user.name || "",
                        email: user.email || "",
                        contactNumber: user.phone || "",
                    });

                } else {
                    setError("Failed to load user profile.");
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load profile data.";
                setError(msg);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: msg,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    <p className="text-gray-600 mt-1">Manage your account details</p>
                </div>
                {!isEditing && (
                    <motion.button
                        onClick={() => setIsEditing(true)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Edit Profile
                    </motion.button>
                )}
            </div>

            <form className="space-y-6">
                {/* Name */}
                <div className="w-full">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {userData.isLawFirm ? "Office Name *" : "Full Name *"}
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder={userData.isLawFirm ? "Enter office name" : "Enter your name"}
                    />
                </div>

                {/* Email (Read-only) */}
                <div className="w-full">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                    </label>
                    <input
                        type="email"
                        id="email"
                        value={formData.email}
                        disabled
                        className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg"
                    />
                </div>

                {/* Contact Number */}
                <div className="w-full">
                    <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Number *
                    </label>
                    <input
                        type="tel"
                        id="contactNumber"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter contact number"
                    />
                </div>
            </form>
        </div>
    );
};

export default OfficePersonalInfo;